import styled from "@emotion/styled";
import { isNft, isNftPrintEdition } from "@metaplex-foundation/js";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { NATIVE_SOL } from "@stepfinance/common";
import { ContextButton, lightGrey2 } from "@stepfinance/react";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import { Tooltip } from "antd";
import type { CheckboxChangeEvent } from "antd/lib/checkbox/Checkbox";
import Checkbox from "antd/lib/checkbox/Checkbox";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { burnToken, getRedeemableTokenBalance } from "../../actions/burn/burnToken";
import { ReactComponent as BurnIcon } from "../../assets/flame.svg";
import { ReactComponent as InfoIcon } from "../../assets/info-round-grey.svg";
import { useLanguageContext } from "../../contexts/language/language";
import type { BurnNotificationStrings } from "../../contexts/language/languageTypes";
import { useConnectedWallet } from "../../hooks";
import { useBurnMetaplexAsset } from "../../hooks/mutations/metaplex/useBurnMetaplexAsset";
import type { CompressedNFT, NFTWithTokenAccount } from "../../models/nfts/nfts";
import { isCompressedNFT, isNFTWithTokenAccount } from "../../models/nfts/nfts";
import { getRedeemableNFTBalance } from "../../utils/nft/utils";
import { handlePreSendTxnError } from "../../utils/transactions";
import { isFungibleToken } from "../../utils/utils";
import { AmountInput } from "../AmountInput/AmountInput";
import { InfoCard } from "../InfoCard/InfoCard";
import type { ModalProps } from "../Modal/Modal";
import { Modal } from "../Modal/Modal";
import { NFTFileDisplay } from "../nft/NFTFileDisplay";
import { ModalImageContainer } from "../shared";

interface BurnModalProps extends Omit<ModalProps, "title" | "children" | "TitleIcon"> {
  data: NFTWithTokenAccount | CompressedNFT | NormalizedTokenAccount | null;
  onSuccess: () => void;
}

export const BurnModal: FC<BurnModalProps> = ({ data, onSuccess, onClose, ...props }) => {
  const [userAgreesTerms, setUserAgreesTerms] = useState(false);
  const [burnAmount, setBurnAmount] = useState("");
  const [amountToReclaim, setAmountToReclaim] = useState("");
  let balance = 0;
  if (data) {
    if (isFungibleToken(data)) {
      balance = data.tokenAmount.uiAmount;
    } else if (isNFTWithTokenAccount(data)) {
      balance = data.associatedTokenAccount.tokenAmount.uiAmount;
    } else if (isCompressedNFT(data)) {
      // cNFTs always have balance of one
      balance = 1;
    }
  }
  const isUserBurningAllTokens = !!burnAmount && Number(burnAmount) >= balance;
  const hasMoreAmountThanBalance = !!burnAmount && Number(burnAmount) > balance;
  const { connection } = useConnection();
  const wallet = useConnectedWallet();

  const burnNftMutation = useBurnMetaplexAsset();

  const {
    currentLanguageData: {
      common,
      components: { burnModal: burnModalText },
    },
  } = useLanguageContext();

  const notificationStrings: BurnNotificationStrings = { ...common.notifications, ...burnModalText.notifications };

  useEffect(() => {
    const fetchTokenAccountBalance = async (data: NormalizedTokenAccount) => {
      if (data) {
        const tokenAccountKey = data.pubkey;
        const redeemableBalance = await getRedeemableTokenBalance(connection, new PublicKey(tokenAccountKey), true);
        setAmountToReclaim(String(redeemableBalance));
      }
    };

    const fetchNFTBalance = async (nft: NFTWithTokenAccount) => {
      if (data) {
        const redeemableBalance = await getRedeemableNFTBalance(connection, nft, true);
        setAmountToReclaim(String(redeemableBalance));
      }
    };

    if (data) {
      if (isFungibleToken(data)) {
        void fetchTokenAccountBalance(data);
      } else if (isNFTWithTokenAccount(data)) {
        void fetchNFTBalance(data);
      } else if (isCompressedNFT(data)) {
        setAmountToReclaim("0.00");
      }
    }

    return () => {
      setAmountToReclaim("");
    };
  }, [data, connection]);

  useEffect(() => {
    setBurnAmount(balance.toString());
  }, [balance]);

  const handleClose = () => {
    setUserAgreesTerms(false);
    onClose();
  };

  const onChange = (event: CheckboxChangeEvent) => {
    setUserAgreesTerms(event.target.checked);
  };

  const onBurnTokenClick = async () => {
    if (wallet && data && burnAmount) {
      // Note: printed editions burned as tokens, since there isn't an easy way to get the original master edition mint.
      const isPrintEdition = isNft(data) && isNftPrintEdition(data.edition) && !isCompressedNFT(data);
      if (isFungibleToken(data) || isPrintEdition) {
        try {
          await burnToken(connection, wallet, Number(burnAmount), data, notificationStrings);
          onSuccess();
        } catch (error) {
          handlePreSendTxnError(error as Error);
        }
      } else if (isNFTWithTokenAccount(data) || isCompressedNFT(data)) {
        burnNftMutation.mutate({ nft: data, amount: Number(burnAmount) });
      }
      handleClose();
    }
  };

  return (
    <Modal onClose={handleClose} {...props} TitleIcon={BurnIcon} title={burnModalText.title}>
      {data && (
        <ContentContainer>
          {isFungibleToken(data) && (
            <div className="token-burn-amount">
              <AmountInput
                headerText={burnModalText.youWillBurn}
                mint={new PublicKey(data.info.mint)}
                amount={burnAmount}
                onAmountChange={(_event, amount) => {
                  setBurnAmount(amount);
                }}
                showMaxButton
              />
            </div>
          )}
          {(isNFTWithTokenAccount(data) || isCompressedNFT(data)) && (
            <>
              <div className="token-burn-amount">
                <AmountInput
                  headerText={burnModalText.youWillBurn}
                  tokenInfoDisplay={`${data.name} (Metaplex)`}
                  amount={burnAmount}
                  mint={data.address}
                  priceOverride={data.floorPriceUSD}
                  onAmountChange={(_event, amount) => {
                    setBurnAmount(amount);
                  }}
                  onlyAllowIntegers
                  showMaxButton
                />
              </div>
              <ModalImageContainer>
                <NFTFileDisplay nft={data} />
              </ModalImageContainer>
            </>
          )}
          {isUserBurningAllTokens && (
            <div className="reclaimed-rent">
              <AmountInput
                displayMinimizedVersion
                headerText={
                  <ReclaimedRentHeader>
                    {burnModalText.reclaimedRentHeader}
                    <Tooltip
                      className="reclaimed-rent-tooltip"
                      title={burnModalText.rentTooltipText}
                      placement="bottom"
                    >
                      <InfoIcon width={16} height={16} />
                    </Tooltip>
                  </ReclaimedRentHeader>
                }
                mint={NATIVE_SOL}
                amount={amountToReclaim}
                disabled
                showBalance={false}
              />
            </div>
          )}
          <div className="token-burn-info">
            <InfoCard type="warn" title={burnModalText.infoCardTitle}>
              <span>{burnModalText.burnWarning}</span>
              <Checkbox className="terms-checkbox" checked={userAgreesTerms} onChange={onChange}>
                {burnModalText.iUnderstandCheckbox}
              </Checkbox>
            </InfoCard>
          </div>
          <div className="buttons-container">
            <ContextButton onClick={handleClose} className="cancel-button" borderless>
              {common.cancelButton}
            </ContextButton>
            <ContextButton onClick={onBurnTokenClick} disabled={!userAgreesTerms || hasMoreAmountThanBalance}>
              {hasMoreAmountThanBalance
                ? burnModalText.buttonText.insufficientBalance
                : burnModalText.buttonText.burnToken}
            </ContextButton>
          </div>
        </ContentContainer>
      )}
    </Modal>
  );
};

const ReclaimedRentHeader = styled.div`
  display: flex;
  align-items: center;
  .reclaimed-rent-tooltip {
    margin-left: 7px;
  }
`;

const ContentContainer = styled.div`
  .token-burn-amount {
    margin-bottom: 16px;
  }
  .reclaimed-rent {
    margin: 16px 0;
  }
  .token-burn-info {
    margin-bottom: 16px;
    .burn-info {
      margin-top: 10px;
    }
    .terms-checkbox {
      margin-top: 10px;
      color: ${lightGrey2};
    }
  }
  .buttons-container {
    margin-top: 32px;
    display: flex;
    justify-content: flex-end;
    .cancel-button {
      margin-right: 8px;
    }
  }
`;
